import styled from '@emotion/styled'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Breakpoint from '../../Breakpoint'
import Label from '../../components/form/Label'
import Loader from '../../components/global/Loader'
import SearchInput from '../../components/global/SearchInput'
import Title from '../../components/global/Title'
import UserContext from '../../context/UserContext'
import apiClient from '../../services/apiClient'
import { CourtType, Exercise, GameType, Intensity } from '../../types/exercise.type'
import { Skill } from '../../types/skill.type'
import { UserContextType } from '../../types/user.type'
import { renderHtmlContent } from '../../utils/htmlRenderer'
import { StyledButton } from '../admin/Login'
import CoachExerciseFormModal from './CoachExerciseFormModal'

const Btn = styled(StyledButton)`
    margin-right: 8px;
    margin-bottom: 8px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin-right: 0;
        width: 100%;
    }
`

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    width: 100%;

    @media (max-width: 750px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`

const FilterContainer = styled.div`
    display: flex;
    gap: 32px;
    margin-bottom: 28px;
    flex-wrap: wrap;
    align-items: flex-end;
    width: 100%;
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }
`

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 200px;
`

const FilterSelect = styled.select`
    padding: 8px 12px;
    border-radius: 4px;
    border: 1px solid #d3d3d3;
    font-size: 1.6rem;
    height: 38px;
    width: 100%;
    background-color: white;

    &:focus {
        border-color: #80bdff;
        outline: 0;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
`

const getGameTypeLabel = (type: GameType, t: any): string => {
    const gameTypeMap: Record<GameType, string> = {
        SINGLES: t('coach.exercises.filters.gameTypeSingles', 'Simple'),
        DOUBLES: t('coach.exercises.filters.gameTypeDoubles', 'Double'),
        BOTH: t('coach.exercises.filters.gameTypeBoth', 'Les deux'),
    }
    return gameTypeMap[type] || type
}

const getCourtTypeLabel = (type: CourtType, t: any): string => {
    const courtTypeMap: Record<CourtType, string> = {
        FULL: t('coach.exercises.filters.courtTypeFull', 'Complet'),
        HALF: t('coach.exercises.filters.courtTypeHalf', 'Demi-terrain'),
        BOTH: t('coach.exercises.filters.courtTypeBoth', 'Les deux'),
    }
    return courtTypeMap[type] || type
}

const getUniqueSkills = (skills: { skill: Skill }[]) => {
    const uniqueSkills = new Map<string, Skill>()

    skills.forEach(({ skill }) => {
        const key = skill.parentSkill?.name ?? skill.name
        if (!uniqueSkills.has(key)) {
            uniqueSkills.set(key, skill)
        }
    })

    return Array.from(uniqueSkills.values())
}

interface ExerciseItemProps {
    ex: Exercise
    onEdit: (ex: Exercise) => void
    onDelete: (id: number) => void
    isDeleting: boolean
    deleteId: number | null
}

const ExerciseItem: React.FC<ExerciseItemProps> = ({ ex, onEdit, onDelete, isDeleting, deleteId }) => {
    const { t } = useTranslation()
    return (
        <ExerciseCard key={'ex-' + ex.id}>
            <CardTitle>{ex.title}</CardTitle>
            <CardMeta>
                <MetaItem>
                    <b>👥</b> {ex.peopleCount}{' '}
                    {t('coach.exercises.exerciseItem.people', {
                        count: ex.peopleCount,
                        defaultValue: 'personne',
                        defaultValue_plural: 'personnes',
                    })}
                </MetaItem>
                <MetaItem>
                    <b>⚡</b> {t('coach.exercises.exerciseItem.intensity')}{' '}
                    <IntensityBadge intensity={ex.intensity}>
                        {t(`coach.exercises.exerciseItem.intensityEnum.${ex.intensity}`)}
                    </IntensityBadge>
                </MetaItem>
                <MetaItem>
                    <b>🏸</b> {t('coach.exercises.exerciseItem.gameType')} {getGameTypeLabel(ex.gameType, t)}
                </MetaItem>
                <MetaItem>
                    <b>📏</b> {t('coach.exercises.exerciseItem.courtType')} {getCourtTypeLabel(ex.courtType, t)}
                </MetaItem>
                {ex.skills?.length > 0 && (
                    <MetaItem>
                        <b>🎯</b> {t('coach.exercises.exerciseItem.skills')}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                            {getUniqueSkills(ex.skills).map((skill) => (
                                <SkillsBadge key={`exSkill-${skill.id}`}>
                                    {skill.parentSkill?.name ?? skill.name}
                                </SkillsBadge>
                            ))}
                        </div>
                    </MetaItem>
                )}
            </CardMeta>
            {ex.description && <CardDesc dangerouslySetInnerHTML={renderHtmlContent(ex.description)} />}
            <CardActions>
                <Btn variant="primary" onClick={() => onEdit(ex)} disabled={isDeleting && deleteId === ex.id}>
                    {t('common.edit')}
                </Btn>
                <Btn variant="danger" onClick={() => onDelete(ex.id)} disabled={isDeleting && deleteId === ex.id}>
                    {isDeleting && deleteId === ex.id ? t('coach.exercises.deleting') : t('common.delete')}
                </Btn>
            </CardActions>
        </ExerciseCard>
    )
}

const CoachExercises: React.FC = () => {
    const { t } = useTranslation()
    const user: UserContextType = useContext(UserContext)
    const queryClient = useQueryClient()
    const [availableIntensities, setAvailableIntensities] = useState<Intensity[]>([])
    const [availablePeopleCounts, setAvailablePeopleCounts] = useState<number[]>([])
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([])
    const [allSkills, setAllSkills] = useState<Skill[]>([])

    // Type pour les compétences dans la réponse de l'API
    type ApiSkill = {
        skill: Skill
    }

    const [filters, setFilters] = useState({
        intensity: '' as Intensity | '',
        gameType: '' as GameType | '',
        courtType: '' as CourtType | '',
        peopleCount: '',
        skillIds: [] as number[],
        searchTerm: '',
    })

    const {
        data: exercises = [],
        isLoading: loading,
        refetch,
    } = useQuery<Exercise[]>({
        queryKey: ['exercises', filters],
        queryFn: async () => {
            const params = new URLSearchParams()
            if (filters.searchTerm) params.append('search', filters.searchTerm)
            if (filters.intensity) params.append('intensity', filters.intensity)
            if (filters.gameType) params.append('gameType', filters.gameType)
            if (filters.courtType) params.append('courtType', filters.courtType)
            if (filters.peopleCount) params.append('peopleCount', filters.peopleCount)
            if (filters.skillIds.length > 0) {
                filters.skillIds.forEach((id) => params.append('skillIds', id.toString()))
            }
            params.append('role', 'ROLE_COACH')

            const response = await apiClient.get(`/exercises?${params.toString()}`)
            return response.data
        },
    })

    // Chargement de toutes les compétences disponibles
    const { data: skills = [] } = useQuery<Skill[]>({
        queryKey: ['all-skills'],
        queryFn: async () => {
            const response = await apiClient.get<Skill[]>('/skills', {
                params: {
                    role: 'ROLE_COACH',
                },
            })
            // Trier les compétences par nom pour une meilleure expérience utilisateur
            const sortedSkills = [...response.data].sort((a, b) => a.name.localeCompare(b.name))
            setAllSkills(sortedSkills)
            return sortedSkills
        },
    })

    useEffect(() => {
        if (exercises.length > 0) {
            // Mise à jour des intensités disponibles
            const intensities = Array.from(new Set(exercises.map((ex: Exercise) => ex.intensity))) as Intensity[]
            setAvailableIntensities(intensities)

            // Mise à jour des nombres de personnes disponibles
            const peopleCounts = Array.from(
                new Set(exercises.map((ex: Exercise) => ex.peopleCount)) as Set<number>,
            ).sort((a: number, b: number) => a - b)
            setAvailablePeopleCounts(peopleCounts)

            // Mise à jour des compétences disponibles basée sur les exercices chargés
            const exerciseSkills = exercises.flatMap((ex: Exercise) => (ex.skills as ApiSkill[]).map((s) => s.skill))
            const uniqueExerciseSkills = Array.from(
                new Map(exerciseSkills.map((skill: Skill) => [skill.id, skill])).values(),
            ) as Skill[]

            // Toujours montrer toutes les compétences disponibles, pas seulement celles des exercices actuels
            // Cela permet de sélectionner n'importe quelle compétence, même si elle n'est pas dans les résultats actuels
            setAvailableSkills((prev: Skill[]) => {
                // Créer un Set des IDs des compétences actuelles pour une recherche plus rapide
                const currentSkillIds = new Set(prev.map((s: Skill) => s.id))

                // Ajouter les nouvelles compétences qui ne sont pas déjà dans la liste
                const newSkills = uniqueExerciseSkills.filter((skill: Skill) => !currentSkillIds.has(skill.id))

                return [...prev, ...newSkills] as Skill[]
            })
        }
    }, [exercises])

    const renderExerciseList = () => {
        if (loading) {
            return <Loader />
        }

        if (exercises.length === 0) {
            return <NoResult>Aucun exercice trouvé.</NoResult>
        }

        return exercises.map((ex: Exercise) => (
            <ExerciseItem
                key={ex.id}
                ex={ex}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isDeleting={isDeleting}
                deleteId={deleteId}
            />
        ))
    }

    const [isModalOpen, setIsModalOpen] = useState(false)
    // Type pour l'exercice en cours d'édition
    type EditExerciseType = Omit<Exercise, 'skills'> & {
        skillIds: number[]
        skills: Array<{ skill: Skill }>
    }

    const [editExercise, setEditExercise] = useState<EditExerciseType>()
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleFormSuccess = useCallback(() => {
        setIsModalOpen(false)
        setEditExercise(undefined)
        queryClient.invalidateQueries({ queryKey: ['exercises'] })
        refetch()
    }, [queryClient, refetch])

    const deleteExerciseMutation = useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(`/exercises/${id}`, {
                params: {
                    role: 'ROLE_COACH',
                },
            })
        },
        onSuccess: () => {
            toast.success(t('coach.exercises.deleteSuccess'))
            queryClient.invalidateQueries({ queryKey: ['exercises'] })
        },
        onError: () => {
            toast.error(t('coach.exercises.deleteError'))
        },
        onSettled: () => {
            setDeleteId(null)
            setIsDeleting(false)
        },
    })

    const handleDelete = useCallback(
        (id: number) => {
            setDeleteId(id)
            setIsDeleting(true)
            deleteExerciseMutation.mutate(id)
        },
        [deleteExerciseMutation],
    )

    const handleCreate = () => {
        setEditExercise(undefined)
        setIsModalOpen(true)
    }

    const handleEdit = (ex: Exercise) => {
        const exerciseCopy = JSON.parse(JSON.stringify(ex)) as Exercise

        // Créer une copie complète de l'exercice avec les IDs de compétences
        const exerciseToEdit: EditExerciseType = {
            ...exerciseCopy,
            skillIds: exerciseCopy.skills.map((skillItem) => skillItem.skill.id),
            skills: [...exerciseCopy.skills], // Conserver la structure originale des compétences
        }

        setEditExercise(exerciseToEdit)
        setIsModalOpen(true)
    }

    return (
        <>
            <TitleWrapper>
                <Title>{t('coach.exercises.title')}</Title>
                <StyledButton className="btn btn-primary" onClick={handleCreate}>
                    {t('coach.exercises.addExercise')}
                </StyledButton>
            </TitleWrapper>
            <CoachExerciseFormModal
                show={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false)
                    setEditExercise(undefined)
                }}
                exercise={editExercise}
                onSuccess={handleFormSuccess}
                skills={skills}
            />
            <FilterContainer>
                <FilterGroup style={{ flex: 1, maxWidth: '300px' }}>
                    <SearchInputCustom
                        value={filters.searchTerm}
                        onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                        placeholder={t('coach.exercises.searchPlaceholder')}
                    />
                </FilterGroup>

                <FilterGroup>
                    <Label>{t('coach.exercises.filters.intensity')}</Label>
                    <FilterSelect
                        id="intensity"
                        value={filters.intensity}
                        onChange={(e) => setFilters({ ...filters, intensity: e.target.value as Intensity })}
                    >
                        <option value="">{t('coach.exercises.filters.all')}</option>
                        {availableIntensities.map((intensity) => (
                            <option key={intensity} value={intensity}>
                                {t(`coach.exercises.exerciseItem.intensityEnum.${intensity}`)}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <Label>{t('coach.exercises.filters.gameType')}</Label>
                    <FilterSelect
                        id="gameType"
                        value={filters.gameType}
                        onChange={(e) => setFilters({ ...filters, gameType: e.target.value as GameType })}
                    >
                        <option value="">{t('coach.exercises.filters.all')}</option>
                        {[GameType.SINGLES, GameType.DOUBLES, GameType.BOTH].map((gameType) => (
                            <option key={gameType} value={gameType}>
                                {getGameTypeLabel(gameType, t)}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <Label>{t('coach.exercises.filters.courtType')}</Label>
                    <FilterSelect
                        id="courtType"
                        value={filters.courtType}
                        onChange={(e) => setFilters({ ...filters, courtType: e.target.value as CourtType })}
                    >
                        <option value="">{t('coach.exercises.filters.all')}</option>
                        {[CourtType.FULL, CourtType.HALF, CourtType.BOTH].map((courtType) => (
                            <option key={courtType} value={courtType}>
                                {getCourtTypeLabel(courtType, t)}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <Label>{t('coach.exercises.filters.peopleCount')}</Label>
                    <FilterSelect
                        value={filters.peopleCount}
                        onChange={(e) => setFilters((f) => ({ ...f, peopleCount: e.target.value }))}
                        className="form-select"
                    >
                        <option value="">{t('coach.exercises.filters.all')}</option>
                        {availablePeopleCounts.map((count) => (
                            <option key={count} value={count}>
                                {count} personne{count > 1 ? 's' : ''}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup style={{ flex: 1, maxWidth: '400px' }}>
                    <Label>{t('coach.exercises.filters.skills')}</Label>
                    <Autocomplete
                        multiple
                        id="skills"
                        options={allSkills}
                        getOptionLabel={(option) => option.name}
                        value={allSkills.filter((skill) => filters.skillIds.includes(skill.id))}
                        filterSelectedOptions
                        renderTags={(value, getTagProps) => {
                            // Grouper les compétences sélectionnées par leur groupe
                            const selectedGroups = new Map<string, number[]>()

                            value.forEach((skill) => {
                                const groupName = skill.parentSkill?.name ?? 'Autres compétences'
                                if (!selectedGroups.has(groupName)) {
                                    selectedGroups.set(groupName, [])
                                }
                                selectedGroups.get(groupName)?.push(skill.id)
                            })

                            // Vérifier pour chaque groupe si toutes les compétences sont sélectionnées
                            const result: React.ReactNode[] = []

                            selectedGroups.forEach((skillIds, groupName) => {
                                const groupSkills = availableSkills.filter(
                                    (s) =>
                                        s.parentSkill?.name === groupName ||
                                        (groupName === 'Autres compétences' && !s.parentSkill),
                                )

                                const allGroupSkillsSelected = groupSkills.every((skill) =>
                                    filters.skillIds.includes(skill.id),
                                )

                                // Toujours afficher le groupe s'il est entièrement sélectionné
                                if (allGroupSkillsSelected) {
                                    result.push(
                                        <span
                                            key={`group-${groupName}`}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                backgroundColor: '#e0e0e0',
                                                borderRadius: '16px',
                                                padding: '4px 12px',
                                                margin: '2px',
                                                fontSize: '1.4rem',
                                            }}
                                        >
                                            {groupName}
                                            <span
                                                style={{
                                                    marginLeft: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '1.6rem',
                                                    lineHeight: '1',
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    const groupSkillIds = groupSkills.map((s) => s.id)
                                                    setFilters((prev) => ({
                                                        ...prev,
                                                        skillIds: prev.skillIds.filter(
                                                            (id) => !groupSkillIds.includes(id),
                                                        ),
                                                    }))
                                                }}
                                            >
                                                ×
                                            </span>
                                        </span>,
                                    )
                                } else {
                                    // Afficher les compétences individuellement pour ce groupe
                                    skillIds.forEach((skillId) => {
                                        const skill = availableSkills.find((s) => s.id === skillId)
                                        if (skill) {
                                            result.push(
                                                <span
                                                    key={skill.id}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        backgroundColor: '#e0e0e0',
                                                        borderRadius: '16px',
                                                        padding: '4px 12px',
                                                        margin: '2px',
                                                        fontSize: '1.4rem',
                                                    }}
                                                >
                                                    {skill.name}
                                                    <span
                                                        style={{
                                                            marginLeft: '8px',
                                                            cursor: 'pointer',
                                                            fontSize: '1.6rem',
                                                            lineHeight: '1',
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                skillIds: prev.skillIds.filter((id) => id !== skill.id),
                                                            }))
                                                        }}
                                                    >
                                                        ×
                                                    </span>
                                                </span>,
                                            )
                                        }
                                    })
                                }
                            })

                            return result
                        }}
                        groupBy={(option) => {
                            const group = option.parentSkill?.name ?? 'Autres compétences'
                            return `${group}_${option.parentSkill?.id}`
                        }}
                        onChange={(_, newValue) => {
                            // S'assurer que les compétences sélectionnées sont bien des objets complets
                            const validSkills = newValue.filter(
                                (skill) => skill && typeof skill === 'object' && 'id' in skill,
                            )

                            // Mettre à jour les filtres sans toucher aux autres valeurs
                            setFilters((prev) => ({
                                ...prev,
                                skillIds: validSkills.map((skill) => skill.id),
                            }))
                        }}
                        disableCloseOnSelect
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={t('coach.exercises.filters.skillsPlaceholder')}
                            />
                        )}
                        renderGroup={(params) => {
                            const groupName = params.group.split('_')[0]
                            const groupSkills = availableSkills.filter(
                                (skill) =>
                                    skill.parentSkill?.name === groupName ||
                                    (groupName === 'Autres compétences' && !skill.parentSkill),
                            )
                            const allGroupSkillsSelected = groupSkills.every((skill) =>
                                filters.skillIds.includes(skill.id),
                            )

                            const toggleGroupSelection = (e: React.MouseEvent) => {
                                e.stopPropagation()
                                const groupSkillIds = groupSkills.map((skill) => skill.id)

                                setFilters((prev) => {
                                    if (allGroupSkillsSelected) {
                                        // Désélectionner toutes les compétences du groupe
                                        return {
                                            ...prev,
                                            skillIds: prev.skillIds.filter((id) => !groupSkillIds.includes(id)),
                                        }
                                    } else {
                                        // Sélectionner toutes les compétences du groupe
                                        return {
                                            ...prev,
                                            skillIds: [...new Set([...prev.skillIds, ...groupSkillIds])],
                                        }
                                    }
                                })
                            }

                            return (
                                <div key={params.key}>
                                    <div
                                        style={{
                                            padding: '8px 12px',
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            borderBottom: '1px solid #e0e0e0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span>{groupName}</span>
                                        <button
                                            type="button"
                                            onClick={toggleGroupSelection}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                padding: '2px 8px',
                                                fontSize: '1.2rem',
                                                cursor: 'pointer',
                                                color: '#555',
                                            }}
                                            title={allGroupSkillsSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                                        >
                                            {allGroupSkillsSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                                        </button>
                                    </div>
                                    <div style={{ padding: '4px 0' }}>{params.children}</div>
                                </div>
                            )
                        }}
                        sx={{
                            width: '100%',
                            background: 'white',
                            '& .MuiInputBase-root': {
                                fontSize: '1.6rem',
                                minHeight: '38px',
                                padding: '4px 8px',
                            },
                            '& .MuiAutocomplete-groupLabel': {
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                backgroundColor: '#f5f5f5',
                                padding: '4px 12px',
                            },
                            '& .MuiAutocomplete-groupUl': {
                                padding: 0,
                            },
                        }}
                    />
                </FilterGroup>
            </FilterContainer>
            <ExerciseList>{renderExerciseList()}</ExerciseList>
        </>
    )
}

export default CoachExercises

const SearchInputCustom = styled(SearchInput)`
    margin: 0;
`

const ExerciseList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`

const CardActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 16px;
    justify-content: flex-end;
`

const ExerciseCard = styled.li`
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    margin-bottom: 16px;
    padding: 20px 24px;
    background: white;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    transition:
        transform 0.2s,
        box-shadow 0.2s;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }
`

const CardTitle = styled.h3`
    margin: 0 0 12px 0;
    font-size: 2rem;
    color: var(--primary-color);
    font-weight: 600;
`

const CardMeta = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin-bottom: 8px;
    justify-content: center;
`

const MetaItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`

interface IntensityBadgeProps {
    intensity: Intensity
}

interface IntensityStyles {
    bg: string
    color: string
}

const getIntensityStyles = (intensity: Intensity): IntensityStyles => {
    switch (intensity) {
        case Intensity.LOW:
            return { bg: '#e6f7e6', color: '#2e7d32' }
        case Intensity.MEDIUM:
            return { bg: '#fff3e0', color: '#e65100' }
        case Intensity.HIGH:
            return { bg: '#ffebee', color: '#c62828' }
    }
}

const IntensityBadge = styled.span<IntensityBadgeProps>(({ intensity }) => {
    const styles = getIntensityStyles(intensity)
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        fontWeight: 500,
        backgroundColor: styles.bg,
        color: styles.color,
    }
})

const CardDesc = styled.div`
    margin: 16px 0 0 0;
    color: #555;
    line-height: 1.5;

    p {
        margin: 0 0 1em 0;
        &:last-child {
            margin-bottom: 0;
        }
    }
`

const SkillsBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #f5f5f5;
    padding: 2px 10px;
    border-radius: 12px;
    color: #616161;
`

const NoResult = styled.div`
    color: #888;
    padding: 32px 0;
    text-align: center;
`
